// oxlint-disable no-console
// libraries
import set from "lodash.set";
import { ErrorCallback, queue, QueueObject } from "async";
import { UploadTaskSnapshot } from "firebase/storage";

// services
import ds from "./persistence.service";
import fs from "./file-system";
import { auth } from "./firebase.init";
import storageService from "./storage.service";
import { _uploadFile, LocalFileRef } from "./file";

// utilities
import { resolveFormValuePath } from "@wombat/anvil/core";
import { Time } from "wombat-global/src/utilities/time";
import { semiRandomId } from "../utilities/ids";
import { traverseObjectPath } from "../utilities/object";

// types
import {
  BrowserFileSystemRef,
  IClientTarget,
  IFileReference,
  ISiteTarget,
  ITenantTarget,
} from "wombat-global/src/typings";

/**
 * persist state to something as durable os the filesystem (localstorage / indexedDsB )
 *
 * Upload service is responsible for managing the connection between the OPFS, document data, and the storageService
 * - queueing up files that are to be uploaded to a bucket and then updating the form data with the process
 * - this is to support offline document storage and better file upload process (interruptive)
 */
export type UploadQueueItem = {
  id: string;
  uid: string | undefined;
  target: IClientTarget | ISiteTarget | ITenantTarget;
  status:
    | "registered"
    | "transfer-queued"
    | "transfer-in-progress"
    | "transfer-errored"
    | "completed";
  error?: unknown;
  process: number;
  removeOn?: number;
  updatedAt?: number;
  completedAt?: number;
  storagePath: string;
  fileSystemRef: BrowserFileSystemRef;
  docRef: {
    target?: IClientTarget | ISiteTarget | ITenantTarget;
    context: string;
    docName?: string;
    docId: string;
    /**
     * post upload docId for newly created instances
     */
    mappedDocId?: string;
    path: string;
  };
  // present only after the transfer is complete
  _storageSnapshot?: Pick<
    UploadTaskSnapshot,
    "bytesTransferred" | "metadata" | "state" | "totalBytes" | "ref"
  >;
  // present only after the transfer is complete
  _storageRef?: string;
};

let uploadServiceCleanupInterval: NodeJS.Timeout | undefined;

class UploadService {
  private taskMap: Record<string, UploadQueueItem>;

  private q: QueueObject<string>;
  private initPromise: Promise<void>;
  private uid: string | undefined;

  public isActive: boolean;
  public isCleaning = false;

  constructor() {
    this.taskMap = {};
    this.isActive = false;

    this.q = queue((t, cb) => this._taskWorker(t, cb), 1);

    this.q.drain(() => {
      console.info("UPLOAD SERVICE: queue drain");
    });

    this.initPromise = this.init();

    /**
     * Run cleanup old tasks every Hour
     * - this runs across all uploads for all users
     */
    if (uploadServiceCleanupInterval) {
      clearInterval(uploadServiceCleanupInterval);
    }
    uploadServiceCleanupInterval = setInterval(() => {
      this.cleanupOldTasks();
    }, Time.hours);
  }

  private async init(): Promise<void> {
    const initData =
      await ds.get<Record<string, UploadQueueItem>>("uploadTasksMap");

    /**
     * if upload stopped while in-progress reset back to queued
     */
    this.taskMap = Object.entries(initData || {}).reduce<
      Record<string, UploadQueueItem>
    >((next, [key, val]) => {
      if (val.status === "transfer-in-progress") {
        next[key] = { ...val, status: "transfer-queued" };
      } else {
        next[key] = val;
      }
      return next;
    }, {});

    /**
     * getCurrent userId to track who uploaded files
     */
    auth.onAuthStateChanged((x) => {
      console.log("UPLOAD SERVICE: reset uid", this.uid, "=>", x?.uid);
      this.uid = x?.uid;
    });
    await auth.authStateReady();
    this.uid = auth.currentUser?.uid;

    /**
     * Only requeue the tasks for the specific user that is logged in.
     */
    setTimeout(() => {
      const ids = Object.values(this.taskMap)
        .filter(
          (x) =>
            x.status === "transfer-queued" && (x.uid === this.uid || !x.uid),
        )
        .map((x) => x.id);
      console.info("UPLOAD SERVICE: requeue incomplete items", ids);
      this.q.push(ids);
    }, 2 * Time.seconds);

    console.info("UPLOAD SERVICE: initialized");
  }

  private async ensureInitialized(): Promise<void> {
    await this.initPromise;
  }

  private async syncData() {
    await ds.set("uploadTasksMap", this.taskMap);
  }

  /**
   * update the task in the queueMap. sync with persistent storage ofter.
   */
  private async _updateTask(
    taskId: string,
    task: Partial<UploadQueueItem>,
  ): Promise<void> {
    if (this.taskMap[taskId]) {
      this.taskMap[taskId] = {
        ...this.taskMap[taskId],
        ...task,
        updatedAt: Date.now(),
      };
      await this.syncData();
    }
  }

  private async cleanupOldTasks() {
    if (this.isCleaning) {
      return;
    }

    await this.ensureInitialized();

    try {
      console.info("UPLOAD SERVICE: starting cleanup");
      this.isCleaning = true;
      await Promise.all(
        Object.values(this.taskMap).map((task) => {
          if (task.status !== "completed") {
            return Promise.resolve();
          }
          console.info("UPLOAD SERVICE: removing upload task", task);

          if (task.removeOn && Date.now() > task.removeOn) {
            return this.removeTask(task.id);
          }

          // remove task that are 14 days older and completed but don't have a removedOn
          if (
            task.completedAt &&
            !task.removeOn &&
            Date.now() > task.completedAt + 14 * Time.days
          ) {
            return this.removeTask(task.id);
          }

          // if cas is complete but theres no removedOn or completedAt time yeet it
          if (!task.completedAt && !task.removeOn) {
            return this.removeTask(task.id);
          }
        }),
      );
      console.info("UPLOAD SERVICE: cleanup done");
    } catch (err) {
      console.error("UPLOAD SERVICE: cleanup failed", err);
    } finally {
      this.isCleaning = false;
    }
  }

  /**
   * Add the mapped ids for post upload enqueue navigation to form / task / training
   *
   * @param source sourceId
   * @param target targetId
   */
  public async setUploadIdsMap(source: string, target: string) {
    await this.ensureInitialized();
    console.log("UPLOAD SERVICE: mapping documentIds", source, target);
    Object.entries(this.taskMap).forEach(([key, val]) => {
      if (val.docRef.docId === source) {
        this.taskMap[key].docRef.mappedDocId = target;
      }
    });
    await this.syncData();
  }

  /**
   * remove upload task (this will not remove tasks that are already queued)
   */
  public async removeTask(taskId: string) {
    console.info("UPLOAD SERVICE: remove task start", taskId);
    await this.ensureInitialized();
    const task = this.taskMap[taskId];
    if (task) {
      await fs.rm(...task.fileSystemRef.path, task.fileSystemRef.name);
    }
    delete this.taskMap[taskId];
    await this.syncData();
    console.info("UPLOAD SERVICE: remove task end", taskId);
  }

  /**
   * Remove all tasks that are saved under the prefix
   *
   * @param prefix formId/taskId/trainingId the prefix of the file system the assets are stored under
   */
  public async removeTasks(prefix: string) {
    console.info("UPLOAD SERVICE: remove tasks start", prefix);
    await this.ensureInitialized();
    const tasks = this.listTasks(prefix);
    await Promise.all(
      tasks.map(async (x) => {
        await fs.rm(...x.fileSystemRef.path, x.fileSystemRef.name);
        delete this.taskMap[x.id];
        await this.syncData();
      }),
    );
    console.info("UPLOAD SERVICE: remove tasks end", prefix);
  }

  /**
   * return the unique id for the task
   */
  public getTaskId(task: { fileSystemRef: BrowserFileSystemRef }) {
    return (
      task.fileSystemRef.path.join(".") +
      "." +
      task.fileSystemRef.name
        .toLocaleLowerCase()
        .replaceAll("_", "-")
        // .replaceAll(".", "-")
        .replaceAll(" ", "-")
        .replaceAll(" ", "-")
        .replaceAll(" ", "-")
    );
  }

  /**
   * list all tasks that start with the prefix
   */
  public listTasks(prefix: string): UploadQueueItem[] {
    return Object.keys(this.taskMap)
      .filter((x) => x.startsWith(prefix))
      .map((x) => this.taskMap[x]);
  }

  /**
   * public method for registering a file to queue up for upload.
   *
   * One complexity is that these items may not have a valid target to update yet.
   * this will happen in any form/task/training instance that has nod be created yet.
   * to handle this we will have to add a ready / not ready and a map to map the OPFS
   * directory (uuid for create form) with the created instance document id.
   *
   * => one solution is the register a map of uuids => document map. when this map is
   * updated make the tasks as ready for upload.
   *
   * Another complication is that the file system will enqueue these task from within the
   * file select. We need to delay the upload (flushing of those tasks) until'l the form is submitted
   *
   * I think the best option is to not flush the queue when registering. But push specific tasks into
   * a processing queue that will be flushed
   */
  public async registerUpload({
    target,
    storagePath,
    docRef,
    file,
    dir,
  }: {
    // target to upload file and update firestore docs
    target: IClientTarget | ITenantTarget | ISiteTarget;
    // storage path for file transfer
    storagePath: string;
    // firebase document target
    docRef: UploadQueueItem["docRef"];
    // file to upload
    file: File;
    // directory to store the file in the browser filesystem
    dir: string[];
  }) {
    await this.ensureInitialized();
    // reference to the file system
    const fileSystemRef = await fs.streamFile(dir, semiRandomId(), file);

    const task: UploadQueueItem = {
      id: this.getTaskId({ fileSystemRef }),
      uid: this.uid,
      target,
      docRef: docRef,
      storagePath,
      fileSystemRef,
      status: "registered",
      process: 0,
    };

    this.taskMap[task.id] = task;
    try {
      await this.syncData();
    } catch (error) {
      delete this.taskMap[task.id];
      await fs.rm(...fileSystemRef.path, fileSystemRef.name);
      throw error;
    }
    console.info("UPLOAD SERVICE: tasks registered", task);

    return task;
  }

  /**
   * Allow the consumer to enqueue tasks that match the file system directory prefix
   * The result is to allow them choose when to start the upload.
   */
  public async enqueueTasks(prefix: string[]) {
    await this.ensureInitialized();
    console.info("UPLOAD SERVICE: task enqueue start", prefix);

    await Promise.all(
      Object.entries(this.taskMap).map(async ([taskId, task]) => {
        const hit = task.fileSystemRef.path
          .join("__")
          .startsWith(prefix.join("__"));

        /**
         * promote and enqueue only registered tasks
         */
        if (hit && task.status === "registered") {
          await this._updateTask(taskId, { status: "transfer-queued" });
          await this.q.push(taskId);
        }
      }),
    );
  }

  public async enqueueTask(taskId: string) {
    await this.ensureInitialized();
    console.info("UPLOAD SERVICE: queue task", taskId);
    const task = Object.values(this.taskMap).find(async (t) => t.id === taskId);
    if (task?.status === "registered" || task?.status === "transfer-errored") {
      await this._updateTask(taskId, { status: "transfer-queued" });
      await this.q.push(taskId);
    }
  }

  public async awaitUpload(
    taskId: string,
  ): Promise<UploadTaskSnapshot | undefined> {
    await this.ensureInitialized();
    if (taskId) {
      console.info("UPLOAD SERVICE: await upload", taskId);
      await this._updateTask(taskId, { status: "transfer-queued" });
      const snap = await this._taskWorker(taskId, () => {});
      console.info("UPLOAD SERVICE: upload complete", snap);
      return snap;
    }
    return undefined;
  }

  /**
   * this method uploads all the files within the context and mutates the formValues array
   */
  public async uploadAllFormFiles(id: string, formValues: object) {
    await this.ensureInitialized();
    const filesToUpload = this.listTasks(id);
    this.isActive = true;

    console.info("UPLOAD SERVICE: upload all tasks", filesToUpload);
    while (filesToUpload.length) {
      const task = filesToUpload.shift();
      if (!task || task.status !== "registered") {
        continue;
      }

      const resolvedFieldPath = resolveFormValuePath(
        task.docRef.path,
        formValues,
      );
      if (resolvedFieldPath === undefined) {
        continue;
      }

      const fieldValues = traverseObjectPath<Array<LocalFileRef>>(
        task.docRef.path,
        formValues,
      );

      if (!fieldValues || !Array.isArray(fieldValues)) {
        continue;
      }

      const idx = fieldValues.findIndex(
        (x) => x.metadata.uploadTaskId === task.id,
      );

      if (idx === -1) {
        continue;
      }

      const snapshot =
        task.status === "registered"
          ? await uploadService.awaitUpload(task.id)
          : undefined;

      set<IFileReference>(formValues, [...resolvedFieldPath.split("."), idx], {
        fileUUID: fieldValues[idx].fileUUID,
        metadata: {
          ...(task?._storageSnapshot?.metadata || {}),
          ...(snapshot?.metadata || {}),
          ...(snapshot?.task?.snapshot?.metadata || {}),
          ...(typeof fieldValues[idx].metadata.transcript === "string"
            ? { transcript: fieldValues[idx].metadata.transcript }
            : {}),
          ...(typeof fieldValues[idx].metadata.editedTranscript === "string"
            ? { editedTranscript: fieldValues[idx].metadata.editedTranscript }
            : {}),
          contentType: snapshot?.metadata.contentType,
        },
        ref: task._storageRef || snapshot?.ref.toString(),
      });
    }
    this.isActive = false;

    console.info("UPLOAD SERVICE: upload all tasks complete");
    return;
  }

  public async enqueueAllFormFiles(
    id: string,
    formValues: object,
    instanceId?: string,
  ) {
    await this.ensureInitialized();
    const filesToUpload = this.listTasks(id);
    console.info("UPLOAD SERVICE: enqueue all", filesToUpload);

    await Promise.all(
      filesToUpload.map(async (task) => {
        /* ignore task enqueueing if its not registered */
        if (!task || task.status !== "registered") {
          return;
        }

        const fieldValues = traverseObjectPath<Array<LocalFileRef>>(
          task.docRef.path,
          formValues,
        );

        // ignore upload if theres no field values
        if (!fieldValues || !Array.isArray(fieldValues)) {
          return;
        }

        const idx = fieldValues.findIndex(
          (x) => x.metadata.uploadTaskId === task.id,
        );

        // ignore upload if theres no matching upload task in the form values
        if (idx === -1) {
          return;
        }

        if (task.status === "registered") {
          this.q.push(task.id);
          await this._updateTask(task.id, { status: "transfer-queued" });
        }
      }),
    );
    console.info("UPLOAD SERVICE: enqueue all complete");
    return;
  }

  /**
   * upload the file stored in the browser file system to the correct storage bucket then push the
   * response object to a new queue to update the document stored in firestore
   */
  private async _taskWorker(
    taskId: string,
    cb: ErrorCallback,
  ): Promise<UploadTaskSnapshot | undefined> {
    try {
      const task = this.taskMap[taskId];
      const _ref = await storageService.getStorageRef(
        task.target,
        task.storagePath,
      );
      console.info("UPLOAD SERVICE: file ref", _ref);

      // read file
      const file = await fs.readFile(
        task.fileSystemRef.path,
        task.fileSystemRef.name,
      );

      console.info("UPLOAD SERVICE: file ref", file);

      if (!file) {
        console.warn("UPLOAD SERVICE: upload task without matching file", task);
      }

      // upload file
      console.info("UPLOAD SERVICE: file upload start", task);
      const res = await _uploadFile(
        _ref,
        task.fileSystemRef.name,
        file,
        async (process, sn) => {
          await this._updateTask(taskId, {
            process,
            status: "transfer-in-progress",
          });
        },
      ).catch(async (err) => {
        console.error("UPLOAD SERVICE: file upload failed", err);
        await this._updateTask(taskId, {
          error: err.toString(),
          status: "transfer-errored",
        });
        throw err;
      });

      console.info("UPLOAD SERVICE: file upload end", task);
      await this._updateTask(taskId, {
        process: 100,
        status: "completed",
        removeOn: Date.now() + 14 * Time.days,
        completedAt: Date.now(),
        _storageSnapshot: {
          ref: JSON.parse(JSON.stringify(res.ref)),
          state: res.state,
          metadata: JSON.parse(JSON.stringify(res.metadata)),
          totalBytes: res.totalBytes,
          bytesTransferred: res.bytesTransferred,
        },
        /**
         * use storage ref .toString() as its serializable
         */
        _storageRef: res.ref.toString(),
      });
      console.info("UPLOAD SERVICE: finished file cleanup");

      cb();
      return res || undefined;
    } catch (err) {
      console.error("UPLOAD SERVICE: task function err", err);
      cb(err as Error);
      return;
    }
  }
}

const uploadService = new UploadService();
export default uploadService;
